import Link from 'next/link'
import {
    ArrowRightIcon,
    BeakerIcon,
    DocumentMagnifyingGlassIcon,
    HomeModernIcon,
} from '@heroicons/react/24/outline'

const questionTypes = [
    {
        title: 'Hardness and scale',
        description: 'White buildup, spotting, and scale are treatment-fit questions. Use the published hardness range as a starting point, then confirm sizing for the home.',
        icon: BeakerIcon,
    },
    {
        title: 'Taste, odour, or chlorine',
        description: 'These concerns are different from hardness. Carbon filtration or RO may be relevant, depending on whether you want treatment at one tap or throughout the home.',
        icon: HomeModernIcon,
    },
    {
        title: 'Plumbing or source-water concerns',
        description: 'Discolouration, older plumbing, a private well, or mixed symptoms may need testing or an on-site review before any product is selected.',
        icon: DocumentMagnifyingGlassIcon,
    },
]

export default function DrinkingWaterQualityExplainer({
    cityName,
    className = '',
    variant = 'default',
}) {
    const isLocationVariant = variant === 'location'
    const placeLabel = cityName || 'your area'

    return (
        <section
            id="drinking-water-quality-explainer"
            className={`border-y border-water-border bg-water-canvas py-14 sm:py-20 ${className}`}
            aria-labelledby="drinking-water-quality-title"
        >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="grid gap-6 lg:grid-cols-[0.8fr_1.2fr] lg:items-end">
                    <div>
                        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-water-blue-dark">
                            {isLocationVariant ? `${placeLabel} water questions` : 'Beyond hardness'}
                        </p>
                        <h2
                            id="drinking-water-quality-title"
                            className="mt-3 text-balance text-3xl font-semibold tracking-[-0.03em] text-water-ink sm:text-4xl"
                        >
                            Separate the water problem before choosing equipment.
                        </h2>
                    </div>
                    <p className="max-w-2xl text-lg leading-8 text-water-muted lg:justify-self-end">
                        A municipal report describes the public system. Conditions inside a property, treatment goals,
                        and installation requirements still determine the right next step.
                    </p>
                </div>

                <div className="mt-9 grid gap-px overflow-hidden rounded-[1.375rem] border border-water-border bg-water-border md:grid-cols-3">
                    {questionTypes.map((item) => {
                        const Icon = item.icon
                        return (
                            <article key={item.title} className="bg-white p-6 sm:p-7">
                                <Icon className="h-6 w-6 text-water-blue" aria-hidden="true" />
                                <h3 className="mt-5 text-xl font-semibold tracking-[-0.02em] text-water-ink">{item.title}</h3>
                                <p className="mt-3 text-sm leading-6 text-water-muted">{item.description}</p>
                            </article>
                        )
                    })}
                </div>

                <div className="mt-7 flex flex-col gap-4 rounded-[1.125rem] bg-water-ink px-6 py-5 text-white sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p className="font-semibold">Not sure which description matches your home?</p>
                        <p className="mt-1 text-sm leading-6 text-slate-300">
                            Share the source water and symptoms. We will help identify the treatment category before you buy.
                        </p>
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row">
                        <Link
                            href="/landings/help-me-choose"
                            className="inline-flex min-h-11 items-center justify-center rounded-full border border-white/20 px-5 text-sm font-semibold text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                        >
                            Help Me Choose
                        </Link>
                        <Link
                            href="/forms/home-assessment"
                            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-white px-5 text-sm font-semibold text-water-ink transition-colors hover:bg-water-aqua focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-water-ink"
                        >
                            Request Free Assessment
                            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
                        </Link>
                    </div>
                </div>

                <p className="mt-5 text-sm leading-6 text-water-muted">
                    Verify system-level information in the current municipal or regional drinking-water report. For
                    property-specific concerns, use targeted testing or professional assessment rather than a citywide estimate.
                </p>
            </div>
        </section>
    )
}
